import { type TFunction } from 'i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import {
  compact,
  exists,
  type FigureInfo,
  FigureInfoHelpers,
  figureInfoKeyLabelI18nKeyMap,
  figureInfoKeys,
  shiftHTable,
  shiftVTable,
  stackHTables,
  stackVTables,
  stackVTablesWithOperator,
  type TextTable,
  toContentOrNull,
} from '~/utils'

// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const createFigureInfoTable = (
  figureInfo: FigureInfo | undefined,
  t: TFunction,
): TextTable => {
  if (!exists(figureInfo)) {
    return []
  }

  return R.pipe(
    figureInfoKeys,
    R.map((figureInfoKey) => {
      const label = t(figureInfoKeyLabelI18nKeyMap[figureInfoKey])

      return match(figureInfoKey)
        .with('groupFilters', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (groupFilters) =>
            stackHTables(
              [[label]],
              stackVTables(
                ...groupFilters.map(({ brands, name: groupFilterName }) =>
                  stackHTables(
                    [[groupFilterName]],
                    shiftVTable(
                      stackVTables(
                        ...brands.map(({ name: brandName, segments }) =>
                          stackHTables([[brandName]], shiftVTable([segments])),
                        ),
                      ),
                    ),
                  ),
                ),
              ),
            ),
          ),
        )
        .with('overlapGroupFilters', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (overlapGroupFilters) =>
            stackHTables(
              [[label]],
              FigureInfoHelpers.toOverlapGroupFiltersTextTable(
                overlapGroupFilters,
              ),
            ),
          ),
        )
        .with('surveyFilter', 'surveyGroupFilter', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (filterTextGroups) => {
            const conditionTable: TextTable = stackVTablesWithOperator(
              'OR',
              ...filterTextGroups.map((filterTexts) =>
                shiftHTable(
                  stackVTablesWithOperator(
                    'AND',
                    ...filterTexts.map(({ name, subName, values }) => [
                      compact([name, subName, `【${values.join()}】`]),
                    ]),
                  ),
                ),
              ),
            )

            return stackHTables([[label]], conditionTable)
          }),
        )
        .otherwise((figureInfoKey) => {
          const value =
            figureInfoKey === 'screenLabel'
              ? FigureInfoHelpers.toScreenLabel(figureInfo)
              : figureInfo[figureInfoKey]

          return toContentOrNull(value, (value) => {
            if (typeof value === 'string') {
              return [[label, value]]
            }

            return stackHTables(
              [[label]],
              value.map((question) => [question]),
            )
          })
        })
    }),
    compact,
    R.flat(),
  )
}

// Type definitions don't need JSDoc
type ComposeTableForCopyParams = {
  figureInfo: FigureInfo | undefined
  sharedUrl: string
  tableForCopy: TextTable
  t: TFunction
}

export const composeTableForCopy = ({
  figureInfo,
  sharedUrl,
  t,
  tableForCopy,
}: ComposeTableForCopyParams): TextTable => {
  const figureInfoTable = createFigureInfoTable(figureInfo, t)

  return [...tableForCopy, [], ...figureInfoTable, ['URL', sharedUrl]]
}
